import { authorizedApiClient } from "./config";

export type MeetingDoc = {
  id: string;
  workspace_id: string;
  action_log_id?: string | null;
  doc_type: string;
  status: string;
  object_type: string;
  object_external_id: string;
  title?: string | null;
  content_markdown?: string | null;
  content_payload?: Record<string, any> | null;
  metadata_payload?: Record<string, any> | null;
  workflow_run_id?: string | null;
  source?: string | null;
  created_by_user_id?: string | null;
  created_by_email?: string | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
};

type RawMeetingDoc = Omit<MeetingDoc, "doc_type"> & {
  doc_type?: string;
};

const normalizeMeetingDoc = (raw: RawMeetingDoc): MeetingDoc => ({
  ...raw,
  doc_type: raw.doc_type || "meeting_prep",
});

export type GetWorkspaceMeetingDocsParams = {
  workspace_id: string;
  object_type: string;
  doc_type?: string;
  object_external_id?: string;
  status?: string;
  latest_per_object?: boolean;
  limit?: number;
  offset?: number;
};

export type MeetingDocsResponse = {
  docs: MeetingDoc[];
};

export const getWorkspaceMeetingDocs = async ({
  workspace_id,
  object_type,
  doc_type,
  object_external_id,
  status,
  latest_per_object = false,
  limit = 100,
  offset = 0,
}: GetWorkspaceMeetingDocsParams): Promise<MeetingDocsResponse> => {
  const response = await authorizedApiClient.get<{ docs: RawMeetingDoc[] }>(
    `/workspace/${workspace_id}/docs/generated`,
    {
      params: {
        object_type,
        doc_type,
        object_external_id,
        status,
        latest_per_object,
        limit,
        offset,
      },
    },
  );

  return {
    docs: (response.data?.docs || []).map(normalizeMeetingDoc),
  };
};

export type GetObjectMeetingDocsParams = {
  workspace_id: string;
  object_type: string;
  object_external_id: string;
  doc_type?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

export const getObjectMeetingDocs = async ({
  workspace_id,
  object_type,
  object_external_id,
  doc_type,
  status,
  limit = 100,
  offset = 0,
}: GetObjectMeetingDocsParams): Promise<MeetingDocsResponse> => {
  const response = await authorizedApiClient.get<{ docs: RawMeetingDoc[] }>(
    `/workspace/${workspace_id}/objects/${object_type}/${object_external_id}/docs/generated`,
    {
      params: {
        doc_type,
        status,
        limit,
        offset,
      },
    },
  );

  return {
    docs: (response.data?.docs || []).map(normalizeMeetingDoc),
  };
};
